import type { ScaleBand } from 'd3-scale';

import { YTickWithTruncation } from './YTickWithTruncation.js';
import { AxisRight } from '../../../core/components/axes/AxisRight.js';
import { DivAxisWrapper } from '../../../core/components/axes/components/DivAxisWrapper.js';
import { axisFontProps } from '../../../core/components/axes/constants.js';
import { getYTickMaxSize } from '../../../core/components/axes/utils/calculate-axis-size.js';
import { getTickLabelPropsForValueAxis } from '../../../core/components/axes/utils/get-tick-label-props.js';
import { isTickLabelSpaceAvailable } from '../../../core/components/axes/utils/is-tick-label-visible.js';
import { ChartGrid } from '../../../core/components/chart-grid/ChartGridRoot.js';
import { useAxesTicks } from '../../hooks/useAxesTicks.js';
import { useVisibleAxesConfig } from '../../hooks/useVisibleAxesConfig.js';
import { useVisibleAxesScales } from '../../hooks/useVisibleAxesScales.js';
import { isCategorical } from '../../types/guards.js';
import { AREAS } from '../../utils/grid-templates.js';

const tickLabelProps = getTickLabelPropsForValueAxis('right');

export function XYChartRightAxisArea() {
  const { rightTicks } = useAxesTicks();
  const { right: rightConfig } = useVisibleAxesConfig();
  const { right: rightScale } = useVisibleAxesScales();

  const tickFormat = (_: unknown, index: number) =>
    rightTicks.formattedTicks[index];

  const isCategoricalAxis = isCategorical(rightConfig?.type || '');

  const areCategoricalTicksNotFittable =
    rightScale &&
    isCategoricalAxis &&
    !isTickLabelSpaceAvailable((rightScale as ScaleBand<string>).step());

  const hideAxis =
    rightConfig === undefined ||
    areCategoricalTicksNotFittable ||
    rightScale === undefined;

  return hideAxis ? null : (
    <ChartGrid.GridArea gridArea={AREAS.rightAxis}>
      {({ availableWidth, availableHeight }) => (
        <DivAxisWrapper
          divAxisStyle={axisFontProps}
          ticks={rightTicks.formattedTicks}
          label={rightConfig.label}
        >
          <AxisRight
            data-testid="value-axis-right"
            scale={rightScale}
            containerSize={availableHeight}
            width={availableWidth}
            tickValues={rightTicks.ticks}
            tickLabelProps={tickLabelProps}
            tickFormat={tickFormat}
            label={rightConfig.label}
            tickComponent={(renderProps) => (
              <YTickWithTruncation
                position="right"
                renderProps={renderProps}
                maxSpaceForTruncation={getYTickMaxSize(
                  availableWidth,
                  rightConfig.label,
                )}
                centerTick={isCategoricalAxis}
              />
            )}
          />
        </DivAxisWrapper>
      )}
    </ChartGrid.GridArea>
  );
}

XYChartRightAxisArea['displayName'] = 'XYChartRightAxisArea';
